import {
  ColumnDef,
  ColumnFiltersState,
  OnChangeFn,
  PaginationState,
  RowData,
  RowSelectionState,
  SortingState,
  VisibilityState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { fuzzyFilter } from './table-utils';

type TableState = {
  columnFilters: ColumnFiltersState;
  columnVisibility: VisibilityState;
  rowSelection: RowSelectionState;
  sorting: SortingState;
  globalFilter: string;
  pagination: PaginationState;
};

type StateChanges = {
  onColumnFiltersChange: OnChangeFn<ColumnFiltersState>;
  onColumnVisibilityChange: OnChangeFn<VisibilityState>;
  onPaginationChange: OnChangeFn<PaginationState>;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  onSortingChange: OnChangeFn<SortingState>;
};

type ManualOptions = {
  manualFiltering: boolean;
  manualPagination: boolean;
  manualSorting: boolean;
};

type ChangeActions = Partial<
  Pick<
    StateChanges,
    'onColumnFiltersChange' | 'onPaginationChange' | 'onSortingChange'
  >
>;

export function useTableModel<T extends RowData>(
  data: T[],
  columns: ColumnDef<T>[],
  state: TableState,
  stateChanges: StateChanges,
  manualOptions: ManualOptions,
  changeActions: ChangeActions,
) {
  return useReactTable({
    data,
    columns,
    state,
    filterFns: { fuzzy: fuzzyFilter },
    enableColumnResizing: true,
    columnResizeMode: 'onEnd',
    enableSortingRemoval: false,
    globalFilterFn: fuzzyFilter,
    getFilteredRowModel: getFilteredRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    ...stateChanges,
    ...manualOptions,
    ...changeActions,
  });
}
